import { Component } from '@theme/component';

const DPR = window.devicePixelRatio || 1;
const INK_SIZE = 160;
const DS = 72;

/**
 * Scent Intensity PDP section, ported line-for-line from the theme's real original
 * per-product template: a central "ink water" canvas blooms drops colored per product,
 * plus Projection (sonar rings), Richness (layered breathing blobs) and Complexity
 * (wobbling hex cluster) each get their own small animated canvas. Only DOM lookups are
 * scoped to `this` instead of `document.getElementById`, and the loop is paused via
 * IntersectionObserver when off-screen — the drawing/animation math is unchanged.
 * @extends {Component}
 */
class ScentIntensityComponent extends Component {
  requiredRefs = ['ink', 'word', 'desc', 'proj', 'rich', 'comp', 'projBar', 'richBar', 'compBar', 'projDesc', 'richDesc', 'compDesc', 'data'];

  connectedCallback() {
    super.connectedCallback();

    this._F = JSON.parse(this.refs.data.textContent);
    this._siT = 0;
    this._inkDrops = [];
    this._running = false;

    const inkCtx = this.refs.ink.getContext('2d');
    this.refs.ink.width = INK_SIZE * DPR;
    this.refs.ink.height = INK_SIZE * DPR;
    this.refs.ink.style.width = `${INK_SIZE}px`;
    this.refs.ink.style.height = `${INK_SIZE}px`;
    inkCtx.scale(DPR, DPR);
    this._inkCtx = inkCtx;

    this._proj = this._setupDim(this.refs.proj);
    this._rich = this._setupDim(this.refs.rich);
    this._comp = this._setupDim(this.refs.comp);

    this.refs.word.textContent = this._F.character;
    this.refs.desc.textContent = this._F.desc;
    this.refs.projDesc.textContent = this._F.projection.desc;
    this.refs.richDesc.textContent = this._F.richness.desc;
    this.refs.compDesc.textContent = this._F.complexity.desc;
    window.setTimeout(() => {
      this.refs.projBar.style.width = `${this._F.projection.pct}%`;
      this.refs.richBar.style.width = `${this._F.richness.pct}%`;
      this.refs.compBar.style.width = `${this._F.complexity.pct}%`;
    }, 600);

    this._intensity = (this._F.projection.pct + this._F.richness.pct + this._F.complexity.pct) / (3 * 100);
    for (let i = 0; i < 8; i++) {
      this._spawnInkDrop();
      const d = this._inkDrops[i];
      d.r = d.maxR * (0.2 + Math.random() * 0.6);
      d.age = 60 + Math.random() * 140;
    }

    this._sonarRings = [];
    this._spawnSonarRing();
    this._sonarInterval = Math.round(210 - this._intensity * 50);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._drawInkWater();
      this._drawProjection();
      this._drawRichness();
      this._drawComplexity();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this._running = entry.isIntersecting;
          if (this._running) this._loop();
        }
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this._running = false;
  }

  _setupDim(canvas) {
    const ctx = canvas.getContext('2d');
    canvas.width = DS * DPR;
    canvas.height = DS * DPR;
    canvas.style.width = `${DS}px`;
    canvas.style.height = `${DS}px`;
    ctx.scale(DPR, DPR);
    return { ctx, cx: DS / 2, cy: DS / 2, r: DS / 2 - 4 };
  }

  _spawnInkDrop() {
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.random() * 8 * this._intensity;
    const cx = INK_SIZE / 2;
    this._inkDrops.push({
      x: cx + Math.cos(angle) * dist,
      y: cx + Math.sin(angle) * dist,
      r: 0,
      maxR: 8 + Math.random() * 62 * this._intensity,
      speed: 0.06 + Math.random() * 0.1 * this._intensity,
      alpha: 0.5 + Math.random() * 0.38,
      color: this._F.inkColors[Math.floor(Math.random() * this._F.inkColors.length)],
      age: 0,
    });
  }

  _drawInkWater() {
    const ctx = this._inkCtx;
    const cx = INK_SIZE / 2;
    const maxR = INK_SIZE / 2 - 6;
    ctx.clearRect(0, 0, INK_SIZE, INK_SIZE);
    const pool = ctx.createRadialGradient(cx, cx, 0, cx, cx, maxR);
    pool.addColorStop(0, 'rgba(225,208,168,0.28)');
    pool.addColorStop(0.65, 'rgba(215,195,150,0.1)');
    pool.addColorStop(1, 'rgba(243,239,232,0)');
    ctx.fillStyle = pool;
    ctx.beginPath();
    ctx.arc(cx, cx, maxR, 0, Math.PI * 2);
    ctx.fill();

    if (this._siT % 160 === 0) this._spawnInkDrop();
    if (this._inkDrops.length > 14) this._inkDrops.shift();

    this._inkDrops.forEach((d) => {
      d.age++;
      if (d.r < d.maxR) d.r += d.speed;
      const lifeAlpha = d.alpha * (1 - Math.max(0, (d.age - 200) / 130));
      if (lifeAlpha <= 0) return;
      const wobble = Math.sin(this._siT * 0.006 + d.age * 0.018) * 1.8;
      const halo = ctx.createRadialGradient(d.x + wobble * 0.2, d.y, 0, d.x, d.y, d.r * 1.6);
      halo.addColorStop(0, `${d.color}00`);
      halo.addColorStop(0.4, d.color + Math.round(lifeAlpha * 18).toString(16).padStart(2, '0'));
      halo.addColorStop(1, `${d.color}00`);
      ctx.beginPath();
      ctx.arc(d.x + wobble * 0.2, d.y, d.r * 1.6, 0, Math.PI * 2);
      ctx.fillStyle = halo;
      ctx.fill();

      const rWobble = d.r * (0.94 + Math.sin(this._siT * 0.005 + d.age * 0.025) * 0.06);
      const grad = ctx.createRadialGradient(d.x + wobble * 0.15, d.y + wobble * 0.1, 0, d.x, d.y, rWobble);
      grad.addColorStop(0, d.color + Math.round(lifeAlpha * 220).toString(16).padStart(2, '0'));
      grad.addColorStop(0.35, d.color + Math.round(lifeAlpha * 160).toString(16).padStart(2, '0'));
      grad.addColorStop(0.7, d.color + Math.round(lifeAlpha * 60).toString(16).padStart(2, '0'));
      grad.addColorStop(1, `${d.color}00`);
      ctx.beginPath();
      ctx.arc(d.x + wobble * 0.15, d.y + wobble * 0.1, rWobble, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
    });

    const core = ctx.createRadialGradient(cx, cx, 0, cx, cx, 14);
    core.addColorStop(0, `rgba(160,100,30,${0.45 + 0.12 * Math.sin(this._siT * 0.012)})`);
    core.addColorStop(0.5, 'rgba(140,80,20,0.2)');
    core.addColorStop(1, 'rgba(120,60,10,0)');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cx, 14, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(cx, cx, maxR, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(154,132,92,0.1)';
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  _spawnSonarRing() {
    this._sonarRings.push({ r: 3, alpha: 0.6, speed: 0.12 + this._intensity * 0.08 });
  }

  _drawProjection() {
    const { ctx, cx, cy, r: maxR } = this._proj;
    ctx.clearRect(0, 0, DS, DS);
    if (this._siT % this._sonarInterval === 0) this._spawnSonarRing();
    if (this._sonarRings.length > 5) this._sonarRings.shift();

    ctx.beginPath();
    ctx.arc(cx, cy, maxR, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(232,90,32,0.07)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    this._sonarRings = this._sonarRings.filter((ring) => {
      ring.r += ring.speed;
      ring.alpha *= 0.988;
      if (ring.r >= maxR || ring.alpha < 0.015) return false;
      const progress = ring.r / maxR;
      ctx.beginPath();
      ctx.arc(cx, cy, ring.r, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(232,90,32,${ring.alpha * (1 - progress * 0.35)})`;
      ctx.lineWidth = 1 - progress * 0.4;
      ctx.stroke();
      return true;
    });

    const src = ctx.createRadialGradient(cx, cy, 0, cx, cy, 6);
    src.addColorStop(0, 'rgba(232,90,32,0.85)');
    src.addColorStop(1, 'rgba(232,90,32,0)');
    ctx.fillStyle = src;
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();
  }

  _drawRichness() {
    const { ctx, cx, cy, r: maxR } = this._rich;
    ctx.clearRect(0, 0, DS, DS);
    const val = this._F.richness.pct / 100;
    const layerCount = Math.round(4 + val * 6);
    for (let i = layerCount; i >= 1; i--) {
      const frac = i / layerCount;
      const layerR = maxR * frac;
      const wobX = Math.sin(this._siT * 0.005 + i * 0.9) * 1.0;
      const wobY = Math.cos(this._siT * 0.004 + i * 1.1) * 0.8;
      const alpha = (0.1 + val * 0.13 * (1 - frac * 0.4)) * (0.85 + 0.15 * Math.sin(this._siT * 0.008 + i * 0.7));
      const r2 = Math.round(120 + frac * 80);
      const g2 = Math.round(50 + frac * 30);
      const b2 = Math.round(80 + frac * 40);
      const g = ctx.createRadialGradient(cx + wobX, cy + wobY, 0, cx, cy, layerR);
      g.addColorStop(0, `rgba(${r2},${g2},${b2},${alpha * 2})`);
      g.addColorStop(0.5, `rgba(${r2},${g2},${b2},${alpha})`);
      g.addColorStop(1, `rgba(${r2},${g2},${b2},0)`);
      ctx.beginPath();
      ctx.arc(cx + wobX * 0.4, cy + wobY * 0.4, layerR, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
    }
    const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR * 0.3);
    core.addColorStop(0, `rgba(168,85,247,${0.75 + 0.15 * Math.sin(this._siT * 0.012)})`);
    core.addColorStop(0.5, 'rgba(140,60,200,0.35)');
    core.addColorStop(1, 'rgba(100,30,160,0)');
    ctx.fillStyle = core;
    ctx.beginPath();
    ctx.arc(cx, cy, maxR * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }

  _drawComplexity() {
    const { ctx, cx, cy, r: maxR } = this._comp;
    ctx.clearRect(0, 0, DS, DS);
    const val = this._F.complexity.pct / 100;
    const blobCount = Math.round(2 + val * 4);
    for (let b = 0; b < blobCount; b++) {
      const bPhase = this._siT * 0.008 + b * 1.3;
      const bR = maxR * (0.3 + val * 0.25 + 0.08 * Math.sin(bPhase));
      const bX = cx + Math.sin(bPhase * 0.7 + b) * maxR * (0.14 + val * 0.14);
      const bY = cy + Math.cos(bPhase * 0.5 + b * 1.2) * maxR * (0.11 + val * 0.11);
      const alpha = (0.07 + val * 0.09) * (0.7 + 0.3 * Math.sin(bPhase));
      ctx.save();
      ctx.translate(bX, bY);
      ctx.beginPath();
      for (let i = 0; i <= 6; i++) {
        const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
        const rVar = bR * (0.86 + 0.14 * Math.sin(this._siT * 0.012 * val * 2 + i * 1.3 + b));
        const px = Math.cos(angle) * rVar;
        const py = Math.sin(angle) * rVar;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = `rgba(255,107,196,${alpha})`;
      ctx.fill();
      ctx.restore();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, maxR, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,107,196,0.07)';
    ctx.lineWidth = 0.5;
    ctx.stroke();
  }

  _loop() {
    const tick = () => {
      if (!this._running) return;
      this._drawInkWater();
      this._drawProjection();
      this._drawRichness();
      this._drawComplexity();
      this._siT++;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
}

if (!customElements.get('scent-intensity-component')) {
  customElements.define('scent-intensity-component', ScentIntensityComponent);
}
